// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { PowerBIReportModel } from "@store/Framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import ZoomIn from "@insite/mobius/Icons/ZoomIn";
import ZoomOut from "@insite/mobius/Icons/ZoomOut";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FlexWrapper, { FlexWrapperProps } from "@store/Library/Components/Toolkit/FlexWrapper";
import Select, { SelectPresentationProps } from "@store/MobiusOverrides/Select/Select";
import constants from "@store/constants";
import useTrackPowerBi from "@store/Framework/GoogleAnalytics/hooks/useTrackPowerBi";
import { getPowerBIReport } from "@store/Framework/Services/PowerBIReportsService";
import InfosBox from "@store/Library/Components/Toolkit/InfosBox";
import LoadingOverlay from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { useLocation } from "@toolkit/Framework/Store/Data/Pages/PageHooks";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import * as models from "powerbi-models";
import React, { ChangeEvent, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

// This module must be loaded later because it will crash in a server side execution (Bugged module)
let PowerBIEmbed: any;
const loadPowerBiModule = () => {
    if (!PowerBIEmbed) {
        // eslint-disable-next-line @typescript-eslint/no-require-imports, global-require
        PowerBIEmbed = require("powerbi-client-react").PowerBIEmbed;
    }
};

export type reportsIdModel = {
    fields: {
        id: string;
        label: string;
        workspaceId: string;
        effectiveIdentityUserName?: string;
        effectiveIdentityRoles?: string;
    };
};

const enum fields {
    reportsId = "reportsId",
    workspaceId = "workspaceId",
    dataTable = "dataTable",
    dataColumn = "dataColumn",
    selectedPageName = "selectedPageName",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.reportsId]: reportsIdModel[];
        [fields.dataTable]: string;
        [fields.dataColumn]: string;
        [fields.selectedPageName]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
});

interface ReportIframeStyles {
    wrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    actionWrapper?: FlexWrapperProps;
    zoomInButton?: ButtonPresentationProps;
    zoomOutButton?: ButtonPresentationProps;
    carrierSelect?: SelectPresentationProps;
    buttonWrapper?: InjectableCss;
}

const iframeHeight = "67rem";

const reportIframeStyles: ReportIframeStyles = {
    wrapper: {
        css: css`
            ${spacingStyles(pxToRem(32), pxToRem(32))}
            height: 100%;
            border-bottom: 3px solid ${getColor("common.border")};

            .reportClass {
                border: unset;
                height: ${iframeHeight};
                padding: ${pxToRem(16, 0)};
            }

            iframe {
                border: unset;
            }
        `,
    },
    contentWrapper: {
        css: css`
            min-height: calc(${iframeHeight} / 2);
        `,
    },
    actionWrapper: {
        gap: pxToRem(6),
        extendedStyles: {
            content: {
                css: css`
                    justify-content: space-between;
                `,
            },
            wrapper: {
                css: css`
                    width: 100%;
                    padding: ${pxToRem(8, 0)};
                `,
            },
        },
    },
    zoomInButton: {
        sizeVariant: "small",
        variant: "tertiary",
        icon: {
            src: ZoomIn,
            position: "right",
        },
        css: css`
            height: ${pxToRem(35)};
            ${breakpoint("sm", "up")} {
                margin-left: ${pxToRem(8)};
            }
        `,
        mergeCss: true,
    },
    zoomOutButton: {
        sizeVariant: "small",
        variant: "tertiary",
        icon: {
            src: ZoomOut,
            position: "right",
        },
        css: css`
            height: ${pxToRem(35)};
        `,
        mergeCss: true,
    },

    buttonWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            align-items: flex-end;
        `,
    },
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const styles = reportIframeStyles;

const PowerBIReport = ({ fields: { reportsId, dataTable, dataColumn, selectedPageName }, language }: Props) => {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const selectedId = params.get("id") || "";
    const defaultSelectedReport: reportsIdModel =
        selectedId?.length > 0 ? reportsId.find(item => item.fields.id === selectedId) || reportsId[0] : reportsId[0];
    const [clientReport, setClientReport] = useState<PowerBIReportModel>();
    const [localDataIsLoading, setLocalDataIsLoading] = useState<boolean>(false);
    const [errorMessages] = useState<any[]>([]);
    const [isError, setIsError] = useState<boolean>(false);
    const [iframeZoomLevel, setIframeZoomLevel] = useState<number>(0.85); // Default value fits desktop view width
    const [zoomOutDisabled, setZoomOutDisabled] = useState<boolean>(false);
    const [zoomInDisabled, setZoomInDisabled] = useState<boolean>(false);
    const [computedFilters, setComputedFilters] = useState<models.ReportLevelFilters[]>([]);
    const [iframeIsLoading, setIframeIsLoading] = useState<boolean>(false);
    const [configErrors, setConfigErrors] = useState<string[]>([]);
    const [selectedReport, setSelectedReport] = useState<reportsIdModel | undefined>({
        ...defaultSelectedReport,
    });
    const [loadedReport, setLoadedReport] = useState<string | undefined>(reportsId[0]?.fields?.id);
    const { isEditing } = useShellContext();
    const trackPowerBi = useTrackPowerBi();

    useEffect(() => {
        loadPowerBiModule();
    }, []);

    useEffect(() => {
        if (!clientReport || loadedReport !== selectedReport?.fields.id) {
            setLocalDataIsLoading(true);
            setIframeIsLoading(true);

            getPowerBIReport({
                reportId: selectedReport?.fields.id || " ",
                workspaceId: selectedReport?.fields.workspaceId || " ",
                effectiveIdentityUserName: selectedReport?.fields.effectiveIdentityUserName,
                effectiveIdentityRoles: selectedReport?.fields.effectiveIdentityRoles,
            })
                .then(result => {
                    const reportData = result;

                    setClientReport(reportData);
                    const shipTos = getShipTos(reportData);
                    setFilters(shipTos);
                    setLoadedReport(selectedReport?.fields?.id);
                })
                .catch(error => {
                    errorMessages.push(error);
                    setLocalDataIsLoading(false);
                    setIframeIsLoading(false);
                });
        }
    }, [clientReport, selectedReport]);

    const getShipTos = (report: PowerBIReportModel) => {
        if (Object.values(report.allowedCustomers).length) {
            return Object.values(report.allowedCustomers)[0];
        }

        return Object.keys(report.allowedCustomers);
    };

    const setFilters = (shipTos: any[]) => {
        setComputedFilters([
            {
                filterType: models.FilterType.Basic,
                $schema: "http://powerbi.com/product/schema#basic",
                target: {
                    table: dataTable,
                    column: dataColumn,
                },
                operator: "In",
                values: shipTos,
            },
        ]);

        setLocalDataIsLoading(false);
    };

    useEffect(() => {
        iframeZoomLevel < 0.35 ? setZoomOutDisabled(true) : setZoomOutDisabled(false);
        iframeZoomLevel > 1.5 ? setZoomInDisabled(true) : setZoomInDisabled(false);
    }, [iframeZoomLevel]);

    useEffect(() => {
        const _configErrors: string[] = [];

        !reportsId ? _configErrors.push("reportId") : null;

        !dataTable ? _configErrors.push("dataTable") : null;
        !dataColumn ? _configErrors.push("dataColumn") : null;

        setConfigErrors(_configErrors);
    }, [reportsId, dataTable, dataColumn]);

    const handleZoomInClick = () => {
        setIframeZoomLevel(iframeZoomLevel + 0.1);
    };

    const handleZoomOutClick = () => {
        setIframeZoomLevel(iframeZoomLevel - 0.1);
    };

    const onSelectChange = (event: ChangeEvent<HTMLSelectElement>) => {
        const findSelectedReport = reportsId.find(x => x.fields.id === event.currentTarget.value);
        setSelectedReport(findSelectedReport);
    };

    if (!reportsId || !dataTable || !dataColumn) {
        if (isEditing) {
            return (
                <InfosBox type="error">
                    <Typography>{translate("Please fully configure the widget before publishing.")}</Typography>
                    <ul>
                        {configErrors.map((error, index) => (
                            <Typography as="li" key={`${error}_${index}`}>
                                {translate(`${error} is missing`)}
                            </Typography>
                        ))}
                    </ul>
                </InfosBox>
            );
        }

        return (
            <InfosBox type="warning">
                <Typography>
                    {siteMessage(
                        "Report_CouldNotLoadReport-MissingConfig",
                        `(${configErrors.toString().split(",").join(", ")})`,
                    )}
                </Typography>
            </InfosBox>
        );
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <LoadingOverlay loading={localDataIsLoading}>
                <StyledWrapper {...styles.contentWrapper}>
                    {errorMessages && isError && (
                        <InfosBox type="error">
                            {errorMessages.map((error, index) => (
                                <Typography key={index}>
                                    {typeof error === "string" ? error : error?.detailedMessage}
                                </Typography>
                            ))}
                        </InfosBox>
                    )}
                    <FlexWrapper {...styles.actionWrapper}>
                        <Select
                            {...styles.carrierSelect}
                            value={selectedReport?.fields?.id || ""}
                            onChange={onSelectChange}
                        >
                            {reportsId &&
                                reportsId.map(o => (
                                    <option value={o.fields.id} key={o.fields.id}>
                                        {o.fields.label}
                                    </option>
                                ))}
                        </Select>
                        <StyledWrapper {...styles.buttonWrapper}>
                            <Button
                                onClick={handleZoomOutClick}
                                disabled={zoomOutDisabled || iframeIsLoading}
                                {...styles.zoomOutButton}
                            >
                                {translate("Zoom Out")}
                            </Button>
                            <Button
                                onClick={handleZoomInClick}
                                disabled={zoomInDisabled || iframeIsLoading}
                                {...styles.zoomInButton}
                            >
                                {translate("Zoom In")}
                            </Button>
                        </StyledWrapper>
                    </FlexWrapper>

                    {PowerBIEmbed && !localDataIsLoading && (
                        <PowerBIEmbed
                            embedConfig={{
                                type: "report",
                                id: clientReport?.embedReportId,
                                embedUrl: clientReport?.embedUrl,
                                accessToken: clientReport?.accessToken,
                                tokenType: models.TokenType.Embed,
                                pageName: selectedPageName,
                                settings: {
                                    filterPaneEnabled: false,
                                    navContentPaneEnabled: false,
                                    background: models.BackgroundType.Transparent,
                                    zoomLevel: iframeZoomLevel,
                                    localeSettings: {
                                        language: language?.languageCode || "en-us",
                                    },
                                    panes: {
                                        purchaseOutlook: {
                                            visible: false,
                                        },
                                    },
                                },
                                filters: [...computedFilters],
                            }}
                            eventHandlers={
                                // Event properties: event: any, report: any
                                new Map([
                                    [
                                        "rendered",
                                        () => {
                                            setIframeIsLoading(false);
                                        },
                                    ],
                                    [
                                        "pageChanged",
                                        (event: any) => {
                                            trackPowerBi({
                                                event_type: "page_changed",
                                                new_page: event.detail.newPage.displayName,
                                            });
                                        },
                                    ],
                                    [
                                        "saved",
                                        () => {
                                            trackPowerBi({
                                                event_type: "saved",
                                            });
                                        },
                                    ],
                                    [
                                        "dataSelected",
                                        (event: any) => {
                                            trackPowerBi({
                                                event_type: "data_selected",
                                                data_selected: event.detail.dataPoints,
                                            });
                                        },
                                    ],
                                    [
                                        "buttonClicked",
                                        (event: any) => {
                                            trackPowerBi({
                                                event_type: "button_clicked",
                                                button_clicks: event.detail.title,
                                            });
                                        },
                                    ],
                                    [
                                        "error",
                                        (event: any) => {
                                            setIsError(true);
                                            errorMessages.push(event?.detail);
                                        },
                                    ],
                                ])
                            }
                            cssClassName="reportClass"
                        />
                    )}
                </StyledWrapper>
            </LoadingOverlay>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(PowerBIReport),
    definition: {
        displayName: "PowerBI Report",
        group: constants.widgetGroup.report,
        allowedContexts: [constants.pageType.myReports, constants.pageType.report],
        fieldDefinitions: [
            {
                name: fields.reportsId,
                displayName: "Report ID (GUID)",
                editorTemplate: "ListField",
                fieldType: "Translatable",
                defaultValue: [],
                getDisplay: item => item.fields.label,
                fieldDefinitions: [
                    {
                        name: "label",
                        displayName: "Label",
                        defaultValue: "",
                        editorTemplate: "TextField",
                    },
                    {
                        name: "id",
                        displayName: "Id",
                        defaultValue: "",
                        editorTemplate: "TextField",
                    },

                    {
                        name: "workspaceId",
                        displayName: "WorkspaceId",
                        defaultValue: "",
                        editorTemplate: "TextField",
                    },
                    {
                        name: "effectiveIdentityUserName",
                        displayName: "Effective Identity User Name",
                        defaultValue: "",
                        editorTemplate: "TextField",
                    },
                    {
                        name: "effectiveIdentityRoles",
                        displayName: "Effective Identity Roles",
                        defaultValue: "",
                        editorTemplate: "TextField",
                    },
                ],
            },
            {
                name: fields.dataTable,
                displayName: "Data Table Name",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
            },
            {
                name: fields.dataColumn,
                displayName: "Data Column Name 'Ship-To Code'",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
            },
            {
                name: fields.selectedPageName,
                displayName: "Selected Page (tab)",
                tooltip: "Must be the tab ID",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
            },
        ],
    },
};

export default widgetModule;
